import { useId } from 'react'
import { GrammarHint } from '@/components/word/GrammarHint'
import {
  PERSON_ROWS,
  describeForm,
  englishPronoun,
  nonFiniteLabel,
  pronounFor,
  stripLeadingPronoun,
  type GrammaticalNumber,
  type Person,
  type VerbGroup,
} from '@/utils/grammar'
import type { WordFormInfo } from '@/types/word'

interface VerbParadigmProps {
  groups: VerbGroup[]
  iso?: string
}

/** One table per tense and mood, each keyed by pronoun rather than by "3rd p. sg." */
export function VerbParadigm({ groups, iso }: VerbParadigmProps) {
  return (
    <div className="space-y-8">
      {groups.map((g) => (
        <GroupSection key={g.key} label={g.label}>
          {g.finite ? <PronounTable forms={g.forms} iso={iso} /> : <NamedForms forms={g.forms} />}
        </GroupSection>
      ))}
    </div>
  )
}

function GroupSection({ label, children }: { label: string; children: React.ReactNode }) {
  const id = useId()
  return (
    <section aria-labelledby={id}>
      <h4 id={id} className="font-mono text-xs uppercase tracking-widest text-muted">
        {label}
      </h4>
      <div className="mt-3">{children}</div>
    </section>
  )
}

/** A label beside a form, with a hairline above. Fixed label width keeps the forms in one column. */
function Row({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[8.5rem_minmax(0,1fr)] items-center gap-x-3 border-t border-line-soft">
      <dt className="min-w-0 py-1">{label}</dt>
      <dd className="min-w-0 py-1 font-serif text-lg text-heading">{children}</dd>
    </div>
  )
}

function rowFor(forms: WordFormInfo[], person: Person, number: GrammaticalNumber): WordFormInfo | undefined {
  return forms.find(
    (f) => (f.grammatical_info.person ?? '2') === person && f.grammatical_info.number === number,
  )
}

/**
 * Singular and plural sit side by side once the container is wide enough, and
 * stack on a phone. Each half is its own list so the rows never interleave.
 */
function PronounTable({ forms, iso }: { forms: WordFormInfo[]; iso?: string }) {
  const halves = (['singular', 'plural'] as const)
    .map((number) => ({
      number,
      rows: PERSON_ROWS.filter((r) => r.number === number)
        .map((r) => ({ ...r, form: rowFor(forms, r.person, number) }))
        .filter((r): r is typeof r & { form: WordFormInfo } => r.form !== undefined),
    }))
    .filter((h) => h.rows.length > 0)

  return (
    <div className="grid gap-x-10 gap-y-2 @lg:grid-cols-2">
      {halves.map((half) => (
        <dl key={half.number}>
          {half.rows.map((r) => {
            const pronoun = pronounFor(iso, r.person, half.number)
            return (
              <Row
                key={r.person}
                label={
                  <GrammarHint
                    label={pronoun}
                    title={englishPronoun(r.person, half.number)}
                    description={describeForm(r.form)}
                  />
                }
              >
                {stripLeadingPronoun(r.form.surface, pronoun)}
              </Row>
            )
          })}
        </dl>
      ))}
    </div>
  )
}

/** Infinitive, participles, supine: a short list of named forms. */
function NamedForms({ forms }: { forms: WordFormInfo[] }) {
  return (
    <dl>
      {forms.map((f) => (
        <Row
          key={f.grammatical_slot}
          label={<span className="flex min-h-11 items-center text-sm text-muted">{nonFiniteLabel(f)}</span>}
        >
          {f.surface}
        </Row>
      ))}
    </dl>
  )
}
